import {useMemo} from 'react';
import {Waypoint} from 'react-waypoint';

import {ContactUsModal} from '@/components/features/contact-us';
import ValuePropsBanner from '@/components/features/home/HomeRoot/ValuePropsBanner';
import CookieMessages from '@/components/global/banners/CookieMessages';
import SystemMessage from '@/components/global/banners/SystemMessage';
import AppBar from '@/components/global/layout/AppBar';
import SelfServiceReportingModal from '@/components/global/modals/SelfServiceReportingModal/SelfServiceReportingModal';
import JumpBackInHomepageProvider from '@/components/global/providers/JumpBackInHomepageProvider/JumpBackInHomepageProvider';
import {type FulfillmentDetailAddressInput} from '@/graphql/types';
import useTracking from '@/hooks/useTracking';
import useValuePropsExperiment from '@/hooks/useValuePropsExperiment';
import useCollectionOfFilters from '../hooks/useCollectionOfFilters';
import CollectionOfFilters from './CollectionOfFilters/CollectionOfFilters';
import BrandsBar from './BrandsBar';
import CaseStudies from './CaseStudies';
import FoodSpend from './FoodSpend';
import Hero from './Hero';
import HomeFooter from './HomeFooter';
import JumpBackInHomepage from './JumpBackInHomepage';
import MealProgram from './MealProgram';
import RestaurantPartner from './RestaurantPartner';

const TOP_OFFSET = '20%';

type HomeRootProps = {
  geoipRecommendedDefaultAddress?: FulfillmentDetailAddressInput | null;
};

type ScrollableSection =
  | 'home-start-order-address-search-bar'
  | 'home-value-props-banner'
  | 'collection-of-filters-section'
  | 'jump-back-in-section'
  | 'meal-program-section'
  | 'logos-section'
  | 'food-spend-section'
  | 'case-studies-section'
  | 'restaurant-partner-section';

const HomeRoot: React.FC<HomeRootProps> = ({geoipRecommendedDefaultAddress}) => {
  const {track} = useTracking();
  const trackedMap = useMemo(() => new Map<string, boolean>(), []);

  const trackComponentView = (section: ScrollableSection) => {
    if (trackedMap.has(section)) {
      return;
    }

    track(`${section}-scroll` as const, {
      category: 'page',
      event: `Scrolled to ${section} on homepage index page`,
      page: 'homepage index',
      type: 'scroll',
      url: window.location.href,
    });

    // eslint-disable-next-line react-hooks/immutability
    trackedMap.set(section, true);
  };

  const {
    shouldShowCollectionOfFilters,
    cards,
    searchAddress,
    loading: collectionOfFiltersLoading,
  } = useCollectionOfFilters();

  const {experimentIsActive: showValuePropsExperiment, isTreatment: isValuePropsTreatment} =
    useValuePropsExperiment();

  return (
    <JumpBackInHomepageProvider>
      <AppBar />
      <ContactUsModal />
      <SelfServiceReportingModal />
      <SystemMessage shouldShowTitle={false} />
      <CookieMessages />
      {/*ezx579::ARC::ValueProps*/}
      <div className="relative z-20">
        <Waypoint
          onEnter={() => trackComponentView('home-start-order-address-search-bar')}
          topOffset={TOP_OFFSET}
        >
          <Hero
            geoipRecommendedDefaultAddress={geoipRecommendedDefaultAddress}
            shouldCropImagesOnScroll={isValuePropsTreatment}
          />
        </Waypoint>
      </div>
      {/*ezx579::ARC::ValueProps*/}
      {showValuePropsExperiment && (
        <ValuePropsBanner onTreatmentShown={() => trackComponentView('home-value-props-banner')} />
      )}
      <JumpBackInHomepage
        onEnter={() => trackComponentView('jump-back-in-section')}
        topOffset={TOP_OFFSET}
      />
      {shouldShowCollectionOfFilters && (
        <Waypoint
          onEnter={() => trackComponentView('collection-of-filters-section')}
          topOffset={TOP_OFFSET}
        >
          <CollectionOfFilters
            cards={cards}
            loading={collectionOfFiltersLoading}
            recommendedDefaultAddress={searchAddress}
          />
        </Waypoint>
      )}
      <main className="text-peppercorn-800">
        <Waypoint onEnter={() => trackComponentView('meal-program-section')} topOffset={TOP_OFFSET}>
          <MealProgram />
        </Waypoint>
        <Waypoint onEnter={() => trackComponentView('food-spend-section')} topOffset={TOP_OFFSET}>
          <FoodSpend />
        </Waypoint>
        <Waypoint onEnter={() => trackComponentView('logos-section')} topOffset={TOP_OFFSET}>
          <BrandsBar />
        </Waypoint>
        <Waypoint
          onEnter={() => trackComponentView('case-studies-section')} // TODO - Update
          topOffset={TOP_OFFSET}
        >
          <CaseStudies />
        </Waypoint>
        <Waypoint
          onEnter={() => trackComponentView('restaurant-partner-section')} // TODO - Update
          topOffset={TOP_OFFSET}
        >
          <RestaurantPartner />
        </Waypoint>
      </main>
      <HomeFooter />
    </JumpBackInHomepageProvider>
  );
};

export default HomeRoot;
